import { useState } from 'react';
import {
  ArrowRightIcon,
  BotIcon,
  CalendarClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleDotIcon,
  MessageSquareIcon,
  PlusIcon,
  RotateCwIcon,
  SquareIcon,
  SparklesIcon,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import type {
  ActivityChange,
  ActivityFieldChange,
  ActivityGroup,
  ActivityNote,
  ActivityRerun,
  ActivitySchedule,
  ActivityStop,
  ActivityUser,
  FeedItem,
} from '../activityFeed';
import { useAddSchedulerNote } from '../hooks/useAddSchedulerNote';
import { lineDiff } from '../lineDiff';
import type { RunActivityEvent } from '../runActivity';

/** Shown before "Show all"; enough to see what just happened without a wall. */
const INITIAL_LIMIT = 5;

const ICON: Record<RunActivityEvent['kind'], LucideIcon> = {
  'fan-out-started': BotIcon,
  'task-created': PlusIcon,
  'entered-step': ArrowRightIcon,
  'run-finished': CircleDotIcon,
};

function Sentence({
  event,
  onOpenTask,
}: {
  event: RunActivityEvent;
  onOpenTask?: (taskId: string) => void;
}) {
  const title =
    event.taskId && event.taskTitle ? (
      <button
        type="button"
        onClick={() => {
          if (event.taskId) onOpenTask?.(event.taskId);
        }}
        // `text-left`: a `<button>` centers its own text by default, which
        // only showed once a long title actually wrapped to a second line.
        className="text-foreground focus-visible:ring-ring rounded-sm text-left font-medium hover:underline focus-visible:ring-3 focus-visible:outline-none"
      >
        {event.taskTitle}
      </button>
    ) : null;

  switch (event.kind) {
    case 'fan-out-started':
      return <>The run started</>;
    case 'task-created':
      return <>Created {title}</>;
    case 'entered-step':
      return (
        <>
          {title} entered <b className="font-medium">{event.stepName}</b>
        </>
      );
    case 'run-finished':
      return (
        <>
          {title} · {event.stepName} run{' '}
          <span className={event.outcome?.colourClass ?? ''}>
            {event.outcome?.label.toLowerCase()}
          </span>
        </>
      );
  }
}

/** What a row says about the subtask, as a link while the subtask exists. */
function SubtaskName({
  id,
  title,
  exists,
  onOpenTask,
}: {
  id: string;
  title: string;
  exists: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  if (!exists) {
    return <del className="text-muted-foreground">{title}</del>;
  }
  return (
    <button
      type="button"
      onClick={() => onOpenTask?.(id)}
      className="text-foreground focus-visible:ring-ring rounded-sm text-left font-medium hover:underline focus-visible:ring-3 focus-visible:outline-none"
    >
      {title}
    </button>
  );
}

function nameOf(user: ActivityUser | null | undefined): string {
  return user?.display_name ?? 'Someone';
}

/** Who did it, always in words: the marker beside it is never the only cue. */
function actorLabel(change: ActivityChange): string {
  if (change.actor_type === 'ai') return 'AI';
  if (change.actor_type === 'system') return 'System';
  return nameOf(change.actor);
}

const VERB: Record<ActivityChange['op'], string> = {
  create: 'created',
  edit: 'edited',
  delete: 'deleted',
  split: 'split',
};

const FIELD_LABEL: Record<string, string> = {
  title: 'Title',
  description: 'Description',
};

function verbOf(change: ActivityChange): string {
  const only = change.changes.length === 1 ? change.changes[0] : null;
  if (change.op === 'edit' && only?.field === 'title') return 'renamed';
  return VERB[change.op];
}

function Marker({ change }: { change?: ActivityChange }) {
  if (change?.actor_type === 'user' && change.actor) {
    return (
      <UserAvatar
        id={change.actor.id}
        displayName={change.actor.display_name}
        email=""
        className="size-5"
      />
    );
  }
  if (change?.actor_type === 'ai') {
    return <SparklesIcon className="size-3" strokeWidth={1.75} />;
  }
  return <CircleDotIcon className="size-3" strokeWidth={1.75} />;
}

/** Added and removed lines, marked with a sign so colour is never the only cue. */
function DescriptionDiff({ field }: { field: ActivityFieldChange }) {
  return (
    <pre
      aria-label="Description changes"
      className="bg-muted/40 mt-1 overflow-x-auto rounded-md p-2 text-xs whitespace-pre-wrap"
    >
      {lineDiff(field.before ?? '', field.after ?? '').map((line, index) => (
        <span key={index} className="block">
          {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '− ' : '  '}
          {line.text}
        </span>
      ))}
    </pre>
  );
}

function FieldRows({ fields }: { fields: ActivityFieldChange[] }) {
  const [diffOpen, setDiffOpen] = useState<string | null>(null);
  return (
    <ul className="flex flex-col gap-0.5 pl-2 text-xs">
      {fields.map((field) => {
        const label = FIELD_LABEL[field.field] ?? field.field;
        if (field.field === 'description') {
          const open = diffOpen === field.field;
          return (
            <li key={field.field}>
              <span className="text-muted-foreground">{label}</span>{' '}
              <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                  setDiffOpen(open ? null : field.field);
                }}
                className="text-foreground underline underline-offset-4"
              >
                {open ? 'Hide changes' : 'View changes'}
              </button>
              {open ? <DescriptionDiff field={field} /> : null}
            </li>
          );
        }
        return (
          <li key={field.field}>
            <span className="text-muted-foreground">{label}</span>{' '}
            {field.before ? <>{field.before} → </> : null}
            <span className="text-foreground">{field.after ?? '—'}</span>
          </li>
        );
      })}
    </ul>
  );
}

function SplitText({
  change,
  onOpenTask,
}: {
  change: ActivityChange;
  onOpenTask?: (taskId: string) => void;
}) {
  const split = change.split;
  if (!split) return null;
  const titles = split.moved.map((m) => m.title).join(', ');
  return split.direction === 'out' ? (
    <>
      moved {titles} to{' '}
      <SubtaskName
        id={change.subtask_id}
        title={change.subtask_title}
        exists={change.subtask_exists}
        onOpenTask={onOpenTask}
      />
    </>
  ) : (
    <>split from {split.other_task_title}</>
  );
}

/** One change on its own line, with its before and after on demand. */
function ChangeRow({
  change,
  onOpenTask,
}: {
  change: ActivityChange;
  onOpenTask?: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const hasDetail = change.op === 'edit' && change.changes.length > 0;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-sm leading-5">
        <span className="text-foreground font-medium">
          {actorLabel(change)}
        </span>{' '}
        {change.op === 'split' ? (
          <SplitText change={change} onOpenTask={onOpenTask} />
        ) : (
          <>
            {verbOf(change)}{' '}
            <SubtaskName
              id={change.subtask_id}
              title={change.subtask_title}
              exists={change.subtask_exists}
              onOpenTask={onOpenTask}
            />
          </>
        )}
        {hasDetail ? (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Hide details' : 'Show details'}
            onClick={() => {
              setOpen((v) => !v);
            }}
            className="text-muted-foreground ml-1 align-middle"
          >
            {open ? (
              <ChevronDownIcon className="size-3" />
            ) : (
              <ChevronRightIcon className="size-3" />
            )}
          </button>
        ) : null}
      </span>
      {hasDetail && open ? <FieldRows fields={change.changes} /> : null}
    </div>
  );
}

function summaryOf(changes: ActivityChange[]): string {
  const count = (op: ActivityChange['op']) =>
    changes.filter((c) => c.op === op).length;
  return (
    [
      ['Deleted', count('delete')],
      ['edited', count('edit')],
      ['created', count('create')],
      ['split', count('split')],
    ] as const
  )
    .filter(([, n]) => n > 0)
    .map(([word, n]) => `${word} ${String(n)}`)
    .join(', ')
    .replace(/^./, (c) => c.toUpperCase());
}

function captionOf(group: ActivityGroup): string | null {
  if (!group.triggered_by) return null;
  const who = `After a rerun by ${group.triggered_by.display_name}`;
  return group.context_comment
    ? `${who} · “${group.context_comment.excerpt}”`
    : who;
}

/** One step execution's agent changes: a summary, and the changes on demand. */
function GroupRow({
  group,
  defaultOpen,
  onOpenTask,
}: {
  group: ActivityGroup;
  defaultOpen: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const caption = captionOf(group);
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="text-foreground flex items-center gap-1 text-left text-sm font-medium"
      >
        {open ? (
          <ChevronDownIcon className="size-3" />
        ) : (
          <ChevronRightIcon className="size-3" />
        )}
        AI · {group.step_name ?? group.step_id}
      </button>
      <span className="text-muted-foreground text-xs">
        {summaryOf(group.changes)}
      </span>
      {caption ? (
        <span
          title={caption}
          className="text-muted-foreground block max-w-full truncate text-xs"
        >
          {caption}
        </span>
      ) : null}
      {open ? (
        <ul className="border-border flex flex-col gap-1.5 border-t pt-2">
          {group.changes.map((change) => (
            <li key={change.id} className="flex flex-col gap-0.5 text-sm">
              <span className="text-muted-foreground">
                {change.op === 'split' ? (
                  <SplitText change={change} onOpenTask={onOpenTask} />
                ) : (
                  <>
                    <span className="capitalize">{VERB[change.op]}</span>{' '}
                    <SubtaskName
                      id={change.subtask_id}
                      title={change.subtask_title}
                      exists={change.subtask_exists}
                      onOpenTask={onOpenTask}
                    />
                  </>
                )}
              </span>
              {change.op === 'edit' ? (
                <FieldRows fields={change.changes} />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** A person's own move on the run: a rerun or a stop. The name is always in words. */
function PersonRow({
  user,
  children,
}: {
  user: ActivityUser | null | undefined;
  children: React.ReactNode;
}) {
  return (
    <span className="text-muted-foreground text-sm leading-5">
      <span className="text-foreground font-medium">
        {nameOf(user ?? null)}
      </span>{' '}
      {children}
    </span>
  );
}

/**
 * A scheduling decision (spec 239): the tasks it started, or that it started none,
 * and its reason. The reason is the model's, so it is plain text, never markup. It stays
 * folded behind "Details" until asked for.
 */
function ScheduleRow({
  entry,
  onOpenTask,
}: {
  entry: ActivitySchedule;
  onOpenTask?: (taskId: string) => void;
}) {
  // The reason is the model's long explanation; the headline says what happened.
  const [open, setOpen] = useState(false);
  return (
    <>
      <span className="text-muted-foreground text-sm leading-5">
        <span className="text-foreground font-medium">Scheduler</span>{' '}
        {entry.started.length === 0 ? (
          'started nothing'
        ) : (
          <>
            started{' '}
            {entry.started.map((task, index) => (
              <span key={task.id}>
                {index > 0 ? ', ' : null}
                <SubtaskName
                  id={task.id}
                  title={task.title ?? 'a deleted task'}
                  exists={task.title != null}
                  onOpenTask={onOpenTask}
                />
              </span>
            ))}
          </>
        )}
      </span>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1 text-xs"
      >
        {open ? (
          <ChevronDownIcon className="size-3" />
        ) : (
          <ChevronRightIcon className="size-3" />
        )}
        {open ? 'Hide details' : 'Details'}
      </button>
      {open ? (
        <span className="text-muted-foreground text-xs break-words whitespace-pre-wrap">
          {entry.reason}
        </span>
      ) : null}
    </>
  );
}

/**
 * The message box under the feed. A message is the scheduler's main instruction at
 * its next decision, which is when a task of the batch finishes.
 */
function SchedulerMessageBox({
  workspaceId,
  taskId,
}: {
  workspaceId: string;
  taskId: string;
}) {
  const [text, setText] = useState('');
  const add = useAddSchedulerNote(workspaceId, taskId);
  const body = text.trim();
  const send = () => {
    if (!body || add.isPending) return;
    add.mutate(body, {
      onSuccess: () => {
        setText('');
      },
    });
  };
  return (
    <form
      aria-label="Message to the scheduler"
      className="flex flex-col gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              send();
            }
          }}
          rows={2}
          maxLength={2000}
          placeholder="Message to the scheduler, e.g. “Run Release notes next”"
          aria-label="Message to the scheduler"
          className="border-input bg-background focus-visible:ring-ring min-h-[2.75rem] w-full resize-y rounded-md border px-2 py-1.5 text-sm focus-visible:ring-3 focus-visible:outline-none"
        />
        <Button type="submit" size="sm" disabled={!body || add.isPending}>
          Send
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">
        {add.isError
          ? add.error.message
          : 'The scheduler follows it at its next decision.'}
      </p>
    </form>
  );
}

/**
 * What a person told the scheduler. Plain text, never markup. Until a decision has
 * read it, it is the scheduler's main instruction, and the row says so.
 */
function NoteRow({ entry }: { entry: ActivityNote }) {
  return (
    <>
      <PersonRow user={entry.author}>
        told the scheduler
        {entry.used ? null : (
          <span className="text-muted-foreground"> · next decision</span>
        )}
      </PersonRow>
      <span className="text-foreground text-sm break-words whitespace-pre-wrap">
        {entry.body}
      </span>
    </>
  );
}

function stepOf(entry: ActivityRerun | ActivityStop): string {
  return entry.step_name ?? entry.step_id;
}

function ProgressText({
  item,
  onOpenTask,
}: {
  item: Extract<FeedItem, { type: 'progress' | 'grouped-progress' }>;
  onOpenTask?: (taskId: string) => void;
}) {
  if (item.type === 'grouped-progress') {
    return (
      <>
        {item.count} subtasks entered{' '}
        <b className="font-medium">{item.stepName}</b>
      </>
    );
  }
  return <Sentence event={item.event} onOpenTask={onOpenTask} />;
}

export interface RunActivityProps {
  feed: FeedItem[];
  onOpenTask?: (taskId: string) => void;
  showProgress: boolean;
  onShowProgressChange: (show: boolean) => void;
  /** Set on a run that has a scheduler: the box that talks to it. */
  scheduler?: { workspaceId: string; taskId: string } | null;
}

/**
 * The run's activity feed, newest first (`buildActivityFeed`): subtask changes
 * and the run's progress. A task's name opens its drawer, the same `?task=`
 * path the board and list use. The first five rows show; the rest are one
 * click away.
 */
export function RunActivity({
  feed,
  onOpenTask,
  showProgress,
  onShowProgressChange,
  scheduler,
}: RunActivityProps) {
  const [showAll, setShowAll] = useState(false);
  const rows = feed.filter((item) => item.type !== 'day');
  const newestGroup = feed.find((item) => item.type === 'ai-group')?.id;

  // Day separators count toward nothing: the limit is on rows, and a
  // separator is kept only when a row of its day is shown.
  const shownRows = new Set(
    (showAll ? rows : rows.slice(0, INITIAL_LIMIT)).map((row) => row.id),
  );
  const shown = feed.filter((item, index) => {
    if (item.type !== 'day') return shownRows.has(item.id);
    const next = feed[index + 1] as FeedItem | undefined;
    return next ? shownRows.has(next.id) : false;
  });

  return (
    <section aria-label="Run activity" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-foreground text-sm font-medium">Activity</h2>
        <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={showProgress}
            onChange={(event) => {
              onShowProgressChange(event.target.checked);
            }}
          />
          Show progress events
        </label>
      </div>
      {scheduler ? (
        <SchedulerMessageBox
          workspaceId={scheduler.workspaceId}
          taskId={scheduler.taskId}
        />
      ) : null}
      <ol className="flex flex-col">
        {shown.map((item) => {
          if (item.type === 'day') {
            return (
              <li
                key={item.id}
                className="text-muted-foreground pt-1 pb-2 text-xs font-medium"
              >
                {item.label}
              </li>
            );
          }
          const progress =
            item.type === 'progress' || item.type === 'grouped-progress';
          return (
            <li key={item.id} className="flex gap-3">
              <span
                aria-hidden="true"
                className="border-border text-muted-foreground bg-background flex size-5 shrink-0 items-center justify-center rounded-full border"
              >
                {item.type === 'progress' ? (
                  <Icon event={item.event} />
                ) : item.type === 'change' ? (
                  <Marker change={item.entry} />
                ) : item.type === 'ai-group' ? (
                  <SparklesIcon className="size-3" strokeWidth={1.75} />
                ) : item.type === 'rerun' ? (
                  <RotateCwIcon className="size-3" strokeWidth={1.75} />
                ) : item.type === 'stop' ? (
                  <SquareIcon className="size-3" strokeWidth={1.75} />
                ) : item.type === 'schedule' ? (
                  <CalendarClockIcon className="size-3" strokeWidth={1.75} />
                ) : item.type === 'note' ? (
                  <MessageSquareIcon className="size-3" strokeWidth={1.75} />
                ) : (
                  <ArrowRightIcon className="size-3" strokeWidth={1.75} />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5 pb-4">
                {progress ? (
                  <span className="text-muted-foreground text-sm leading-5">
                    <ProgressText item={item} onOpenTask={onOpenTask} />
                  </span>
                ) : item.type === 'change' ? (
                  <ChangeRow change={item.entry} onOpenTask={onOpenTask} />
                ) : item.type === 'rerun' ? (
                  <PersonRow user={item.entry.triggered_by}>
                    reran{' '}
                    <b className="text-foreground font-medium">
                      {stepOf(item.entry)}
                    </b>
                  </PersonRow>
                ) : item.type === 'stop' ? (
                  <PersonRow user={item.entry.stopped_by}>
                    stopped{' '}
                    <b className="text-foreground font-medium">
                      {stepOf(item.entry)}
                    </b>
                  </PersonRow>
                ) : item.type === 'schedule' ? (
                  <ScheduleRow entry={item.entry} onOpenTask={onOpenTask} />
                ) : item.type === 'note' ? (
                  <NoteRow entry={item.entry} />
                ) : (
                  <GroupRow
                    group={item.group}
                    defaultOpen={item.id === newestGroup}
                    onOpenTask={onOpenTask}
                  />
                )}
                {item.type === 'progress' && item.event.detail ? (
                  <span className="text-board-state-failed text-xs">
                    {item.event.detail}
                  </span>
                ) : null}
                <time
                  dateTime={item.at}
                  title={formatExactTime(item.at)}
                  className="text-muted-foreground text-xs"
                >
                  {formatRelativeTime(item.at)}
                </time>
              </span>
            </li>
          );
        })}
      </ol>
      {rows.length > INITIAL_LIMIT ? (
        <button
          type="button"
          aria-expanded={showAll}
          onClick={() => {
            setShowAll((all) => !all);
          }}
          className="text-muted-foreground hover:text-foreground w-fit text-xs underline underline-offset-4"
        >
          {showAll ? 'Show fewer' : `Show all ${String(rows.length)}`}
        </button>
      ) : null}
    </section>
  );
}

function Icon({ event }: { event: RunActivityEvent }) {
  const Glyph = ICON[event.kind];
  return <Glyph className="size-3" strokeWidth={1.75} />;
}
